'use client'

import type { CSSProperties, ReactNode } from 'react'
import Link from 'next/link'
import { useSigmaSurface } from '@/components/SigmaSurfaceProvider'
import { SparklineMicro } from '@/components/sigma/SparklineMicro'
import styles from './MetricCard.module.css'

type MetricDirection = 'higher' | 'lower'

const cardStyle: CSSProperties = {
  background: 'var(--sigma-surface, #ffffff)',
  border: '1px solid var(--sigma-border, #e5e7eb)',
  borderRadius: 'var(--sigma-radius-lg, 12px)',
  padding: '20px',
  minHeight: '132px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  boxSizing: 'border-box',
}

const labelStyle: CSSProperties = {
  fontSize: '14px',
  color: 'var(--sigma-muted, #475467)',
  margin: 0,
  minWidth: 0,
  alignSelf: 'stretch',
}

const valueStyle: CSSProperties = {
  fontWeight: 600,
  lineHeight: 1.15,
  alignSelf: 'stretch',
  /* nowrap + .body overflow-x: auto — full number visible */
  whiteSpace: 'nowrap',
}

const comparisonStyle: CSSProperties = {
  fontSize: '13px',
  margin: 0,
  minWidth: 0,
  alignSelf: 'stretch',
  minHeight: '1.25rem',
  lineHeight: 1.25,
}

function formatComparison(
  current: number,
  previous: number,
  higherIsBetter: boolean,
  format: 'number' | 'currency' | 'percent'
): { text: string; tone: 'success' | 'critical' | 'subdued' } | null {
  if (previous === 0 && current === 0) return null
  if (previous === 0 && current > 0) {
    const isGood = higherIsBetter
    return { text: 'New', tone: isGood ? 'success' : 'critical' }
  }
  if (previous > 0 && current === 0) {
    const isGood = !higherIsBetter
    return { text: '-100% vs previous period', tone: isGood ? 'success' : 'critical' }
  }
  if (previous === 0) return null

  const pct = ((current - previous) / previous) * 100
  if (!Number.isFinite(pct) || Number.isNaN(pct)) return null
  const absPct = Math.abs(pct)
  const pctStr = absPct >= 0.1 ? pct.toFixed(1) : '0'
  const sign = pct >= 0 ? '+' : ''
  const text = `${sign}${pctStr}% vs previous period`

  const improved = higherIsBetter ? pct > 0 : pct < 0
  return {
    text,
    tone: improved ? 'success' : pct === 0 ? 'subdued' : 'critical',
  }
}

function fmtDeltaSignedNumber(delta: number): string {
  const sign = delta >= 0 ? '+' : '−'
  return `${sign}${Math.abs(delta).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
}

function fmtDeltaSignedCurrency(delta: number): string {
  const sign = delta >= 0 ? '+' : '−'
  const abs = Math.abs(delta)
  const n = `Rs ${abs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  return `${sign}${n}`
}

/** Compact absolute delta + percent; same edge cases as formatComparison for previous/current zeros. */
function formatComparisonWithAbsolute(
  current: number,
  previous: number,
  higherIsBetter: boolean,
  format: 'number' | 'currency'
): { text: string; tone: 'success' | 'critical' | 'subdued' } | null {
  if (previous === 0 && current === 0) return null

  if (previous === 0 && current > 0) {
    const isGood = higherIsBetter
    return { text: 'New', tone: isGood ? 'success' : 'critical' }
  }

  if (previous === 0) return null

  if (previous > 0 && current === 0) {
    const isGood = !higherIsBetter
    const absPart = format === 'currency' ? fmtDeltaSignedCurrency(-previous) : fmtDeltaSignedNumber(-previous)
    return {
      text: `${absPart} (−100%)`,
      tone: isGood ? 'success' : 'critical',
    }
  }

  const delta = current - previous
  if (delta === 0) {
    return { text: 'No change', tone: 'subdued' }
  }

  const pct = (delta / previous) * 100
  if (!Number.isFinite(pct) || Number.isNaN(pct)) return null

  const absPart = format === 'currency' ? fmtDeltaSignedCurrency(delta) : fmtDeltaSignedNumber(delta)
  const absPct = Math.abs(pct)
  const pctInParens =
    absPct >= 0.1
      ? `(${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%)`
      : '(0%)'
  const text = `${absPart} ${pctInParens}`

  const improved = higherIsBetter ? pct > 0 : pct < 0
  return {
    text,
    tone: improved ? 'success' : 'critical',
  }
}

function getArrow(tone: 'success' | 'critical' | 'subdued'): string {
  if (tone === 'success') return '\u2191'
  if (tone === 'critical') return '\u2193'
  return '\u2014'
}

interface MetricCardProps {
  label: string
  value: string | number
  previous?: number | null
  currentNum?: number
  higherIsBetter: MetricDirection
  format?: 'number' | 'currency' | 'percent'
  valueTone?: 'success' | 'critical' | 'subdued'
  /** When set, the whole card is a link (dashboard drill-down). */
  href?: string
  /**
   * Optional control shown in a row above the bordered card (never inside the Link).
   * Use for secondary actions while keeping the full card body as the drill-down target.
   */
  accessory?: ReactNode
  /** When true, comparison line shows signed absolute change plus percent (number/currency only). */
  showAbsoluteDelta?: boolean
  /** Optional left accent (semantic emphasis; no top “label bar”). */
  accent?: 'profit' | 'caution'
  /** Optional micro trend (e.g. last N days); hidden if &lt; 2 points. */
  sparklineValues?: number[]
  /** Stronger typographic weight for the primary KPI (e.g. profit). */
  valueEmphasis?: boolean
}

export function MetricCard({
  label,
  value,
  previous,
  currentNum,
  higherIsBetter,
  format = 'number',
  valueTone,
  href,
  accessory,
  showAbsoluteDelta = false,
  accent,
  sparklineValues,
  valueEmphasis,
}: MetricCardProps) {
  const surface = useSigmaSurface()
  const isPortal = surface === 'portal'

  const dir = higherIsBetter === 'higher'
  const comp =
    previous != null && currentNum != null
      ? showAbsoluteDelta && format !== 'percent'
        ? formatComparisonWithAbsolute(currentNum, previous, dir, format)
        : formatComparison(currentNum, previous, dir, format)
      : null

  const valueColor =
    valueTone === 'success'
      ? isPortal
        ? '#047857'
        : '#008060'
      : valueTone === 'critical'
        ? isPortal
          ? '#b91c1c'
          : '#d82c0d'
        : isPortal
          ? '#0f172a'
          : undefined

  const accentClass =
    accent === 'profit' ? styles.cardAccentProfit : accent === 'caution' ? styles.cardAccentCaution : ''

  const resolvedCardStyle: CSSProperties = isPortal
    ? {
        ...cardStyle,
        background: '#ffffff',
        border: '1px solid #e5e7eb',
        boxShadow: 'var(--sigma-portal-shadow-card, 0 1px 2px rgba(15,23,42,0.04), 0 4px 16px rgba(15,23,42,0.06))',
      }
    : cardStyle

  const resolvedLabelStyle: CSSProperties = isPortal
    ? {
        ...labelStyle,
        color: '#64748b',
        fontWeight: 600,
        fontSize: '12px',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
      }
    : labelStyle

  const sparkStroke =
    valueTone === 'success'
      ? 'var(--sigma-semantic-profit, #047857)'
      : valueTone === 'critical'
        ? 'var(--sigma-semantic-risk, #b42318)'
        : 'var(--sigma-semantic-info, #2563eb)'

  const inner = (
    <div className={styles.body}>
      <p style={resolvedLabelStyle}>{label}</p>
      <p
        className={`${styles.value} ${valueEmphasis ? styles.valueEmphasis : ''}`}
        style={{ ...valueStyle, color: valueColor }}
      >
        {value}
      </p>
      {sparklineValues && sparklineValues.length >= 2 ? (
        <div className={styles.sparklineRow}>
          <SparklineMicro values={sparklineValues} stroke={sparkStroke} width={140} height={20} />
        </div>
      ) : null}
      {comp ? (
        <p
          className={styles.comparison}
          style={{
            ...comparisonStyle,
            fontWeight: isPortal ? 600 : undefined,
            color:
              comp.tone === 'success'
                ? isPortal
                  ? '#047857'
                  : '#008060'
                : comp.tone === 'critical'
                  ? isPortal
                    ? '#b91c1c'
                    : '#d82c0d'
                  : isPortal
                    ? '#475569'
                    : '#6b7280',
          }}
        >
          {getArrow(comp.tone)} {comp.text}
        </p>
      ) : (
        <span className={styles.comparisonPlaceholder} style={comparisonStyle} aria-hidden />
      )}
    </div>
  )

  if (href) {
    const link = (
      <Link
        href={href}
        prefetch={false}
        className={`${styles.wrapper} ${styles.cardFace} ${accentClass} ${styles.drilldownLink} ${accessory ? styles.metricCardFlexGrow : ''}`}
        style={{ ...resolvedCardStyle, textDecoration: 'none', color: 'inherit' }}
        aria-label={`${label}: open details`}
      >
        {inner}
      </Link>
    )
    if (accessory) {
      return (
        <div className={styles.withAccessory}>
          <div className={styles.accessoryAbove}>{accessory}</div>
          {link}
        </div>
      )
    }
    return link
  }

  const staticCard = (
    <div
      className={`${styles.wrapper} ${styles.cardFace} ${accentClass} ${accessory ? styles.metricCardFlexGrow : ''}`}
      style={resolvedCardStyle}
    >
      {inner}
    </div>
  )
  if (accessory) {
    return (
      <div className={styles.withAccessory}>
        <div className={styles.accessoryAbove}>{accessory}</div>
        {staticCard}
      </div>
    )
  }
  return staticCard
}
